import React, { useEffect, useState } from 'react';
import { Bell, MapPin, ExternalLink } from 'lucide-react';

/**
 * Righe "Promemoria" e "Posizione" del drawer task (spunti Trello, partner Unidevs).
 * Stesso stile delle altre righe campo del drawer: sfondo #F1F1EF, icona a sinistra.
 */

export const REMINDER_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: null, label: 'Promemoria: il giorno prima' },
  { value: 0, label: 'Promemoria: il giorno stesso' },
  { value: 2, label: 'Promemoria: 2 giorni prima' },
  { value: 3, label: 'Promemoria: 3 giorni prima' },
  { value: 7, label: 'Promemoria: 1 settimana prima' },
  { value: -1, label: 'Nessun promemoria' },
];

const rowClass =
  'w-full flex items-center bg-[#F1F1EF] dark:bg-gray-800 rounded-md pl-2 pr-2 py-1.5 hover:bg-[#EAEAEA] dark:hover:bg-gray-700 transition-colors';

interface TaskReminderFieldProps {
  value: number | null | undefined;
  hasDueDate: boolean;
  onChange: (value: number | null) => void;
}

export function TaskReminderField({ value, hasDueDate, onChange }: TaskReminderFieldProps) {
  const current = value === undefined ? null : value;
  return (
    <div
      className={`relative inline-flex items-center rounded-md ${hasDueDate ? 'bg-[#F1F1EF] dark:bg-gray-800' : 'bg-[#F7F6F3] dark:bg-gray-800'}`}
      title={hasDueDate ? 'Quando avvisare l\'assegnatario prima della scadenza' : 'Imposta prima una scadenza'}
    >
      <Bell className="absolute left-2 h-3 w-3 text-[#787774] pointer-events-none" />
      <select
        disabled={!hasDueDate}
        value={current === null ? '' : String(current)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className="w-full pl-6 pr-2 py-1.5 bg-transparent text-[#37352F] dark:text-gray-100 rounded-md hover:bg-[#EAEAEA] dark:hover:bg-gray-700 focus:outline-none cursor-pointer appearance-none truncate transition-colors disabled:text-[#9B9A97] disabled:cursor-not-allowed"
      >
        {REMINDER_OPTIONS.map(opt => (
          <option key={String(opt.value)} value={opt.value === null ? '' : String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface TaskLocationFieldProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
}

export function mapsUrl(location: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
}

export function TaskLocationField({ value, onChange }: TaskLocationFieldProps) {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = () => {
    const next = draft.trim() || null;
    if ((value || null) !== next) onChange(next);
  };

  return (
    <div className="col-span-2">
      <div className={rowClass}>
        <MapPin className="h-3 w-3 text-[#787774] mr-2 flex-shrink-0" />
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="Posizione (indirizzo, sede, luogo)"
          className="flex-1 min-w-0 bg-transparent text-[#37352F] dark:text-gray-100 placeholder:text-[#9B9A97] dark:placeholder:text-gray-500 outline-none"
        />
        {value && (
          <a
            href={mapsUrl(value)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="ml-2 inline-flex items-center gap-1 text-[11px] text-[#2383E2] hover:underline flex-shrink-0"
            title="Apri in Google Maps"
          >
            <ExternalLink className="h-3 w-3" />
            Mappa
          </a>
        )}
      </div>
      {value && (
        // Anteprima mappa senza chiavi API: embed pubblico di Google Maps
        <iframe
          title="Mappa"
          src={`https://www.google.com/maps?q=${encodeURIComponent(value)}&output=embed`}
          className="mt-1.5 w-full h-36 rounded-md border border-[rgba(55,53,47,0.09)] dark:border-gray-700"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      )}
    </div>
  );
}
